/**
 * Configuration Error Boundary
 * 
 * Displays error messages when required configuration is missing
 * Requirements: 17.4
 */

import { Component } from 'react';
import type { ReactNode } from 'react';
import { Box, Container, Header, Alert, SpaceBetween } from '@cloudscape-design/components';
import { validateConfig } from '../../lib/config';
import type { ConfigValidationResult } from '../../lib/config';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  validationResult: ConfigValidationResult | null;
}

export class ConfigurationErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      validationResult: null,
    };
  }

  static getDerivedStateFromError(): State {
    return {
      hasError: true,
      validationResult: null,
    };
  }

  componentDidMount() {
    // Validate configuration on mount
    const validationResult = validateConfig();
    
    // Only block the app if we're in production and have critical errors
    // In development, log warnings but allow the app to continue
    const isDev = import.meta.env.MODE === 'development';
    
    if (!validationResult.valid && !isDev) {
      // Production: block the app if configuration is invalid
      this.setState({
        hasError: true,
        validationResult,
      });
    } else if (!validationResult.valid || validationResult.warnings.length > 0) {
      // Development: log to console but don't block
      console.warn('Configuration validation issues detected:');
      if (validationResult.errors.length > 0) {
        console.warn('Errors:', validationResult.errors);
      }
      if (validationResult.warnings.length > 0) {
        console.warn('Warnings:', validationResult.warnings);
      }
      console.warn('App will continue in development mode. Check Settings view for details.');
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Configuration error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const validationResult = this.state.validationResult || validateConfig();

      return (
        <Box padding="xxl">
          <Container>
            <SpaceBetween size="l">
              <Header variant="h1">Configuration Error</Header>
              
              <Alert
                type="error"
                header="Missing Required Configuration"
              >
                The application cannot start because required configuration values are missing.
                Please contact your system administrator.
              </Alert>

              {validationResult.errors.length > 0 && (
                <Box>
                  <Header variant="h3">Required Configuration</Header>
                  <SpaceBetween size="xs">
                    {validationResult.errors.map((error, index) => (
                      <Alert key={index} type="error">
                        {error}
                      </Alert>
                    ))}
                  </SpaceBetween>
                </Box>
              )}

              {validationResult.warnings.length > 0 && (
                <Box>
                  <Header variant="h3">Optional Configuration</Header>
                  <SpaceBetween size="xs">
                    {validationResult.warnings.map((warning, index) => (
                      <Alert key={index} type="warning">
                        {warning}
                      </Alert>
                    ))}
                  </SpaceBetween>
                </Box>
              )}

              <Box variant="p" color="text-body-secondary">
                <strong>For Developers:</strong> Configuration should be provided via:
                <ul>
                  <li>Production: <code>window.CONFIG</code> object (loaded from config.js)</li>
                  <li>Development: Environment variables (VITE_* prefix)</li>
                </ul>
              </Box>
            </SpaceBetween>
          </Container>
        </Box>
      );
    }

    return this.props.children;
  }
}
